import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Grid, LinearProgress, Typography } from "@mui/material";

import CUBook from "./CUBook";
import { apiCall } from "../utils/apiCall";
import { useAsync } from "../utils/useAsync";
import { useAuth } from "../contexts/AuthContext";
import DeleteDialog from "../components/DeleteDialog";
import ShowItem from "../components/ShowItem/ShowItem";
import { useSnackbar } from "../contexts/SnackbarProvider";
import TopSection from "../components/TopSection";
import HomeCover from "../assets/images/CoverHeaderLeft.jpg";
import HomeMobileCover from "../assets/images/coverMobile.jpg";
import "../styles/HomePage.css";
import { filterItems } from "../utils/filterItem";
import useIsSmallScreen from "../utils/useIsSmallScreen";

const HomePage = () => {
  const IS_SMALL_SCREEN = useIsSmallScreen();

  const Auth = useAuth();
  const navigate = useNavigate();
  const { setSnackbarDetail } = useSnackbar();

  const [actionType, setActionType] = useState("");
  const [selectedBook, setSelectedBook] = useState();
  const [refetchBooks, setRefetchBooks] = useState(1);
  const [searchText, setSearchText] = useState("");

  const { run: getTotal, data: total, isLoading: isLoadinTotal } = useAsync();

  useEffect(() => {
    getTotal(apiCall({ entity: "api/count/items", method: "get" }));
  }, [getTotal]);
  const {
    run: getAllBooks,
    data: allBooks,
    status: allBooksStatus,
    isLoading: isLoadingBooks,
  } = useAsync({ books: [] });

  useEffect(() => {
    getAllBooks(apiCall({ entity: "api/books", method: "get" }));
  }, [getAllBooks, refetchBooks]);

  async function deleteBook() {
    Auth.AuthenticatedServerCall({
      method: "DELETE",
      entity: `api/books/${selectedBook.id}`,
    })
      .then((res) => {
        setSnackbarDetail({
          open: true,
          message: res.message,
          severity: "success",
        });
        setRefetchBooks((i) => i + 1);
      })
      .catch((error) => {
        setSnackbarDetail({
          open: true,
          message: error.message,
          severity: "error",
        });
      });
    setActionType();
    setSelectedBook();
  }

  if (actionType === "create") {
    return (
      <CUBook
        onBack={(refresh) => {
          setActionType();
          if (refresh) {
            setRefetchBooks((prev) => prev + 1);
          }
        }}
      />
    );
  }

  if (actionType === "edit") {
    return (
      <CUBook
        onBack={(refresh) => {
          setActionType(false);
          if (refresh) {
            setRefetchBooks((prev) => prev + 1);
          }
        }}
        book={selectedBook}
      />
    );
  }

  return (
    <Box className="md-home ">
      <Box className="md-homePage-container">
        <TopSection
          title="Live The Language!"
          searchText={searchText}
          setSearchText={setSearchText}
          numberOfBooks={total?.booksCount}
          numberOfVoices={total?.buttonsCount}
          cover={IS_SMALL_SCREEN ? HomeMobileCover : HomeCover}
        />
        <DeleteDialog
          open={actionType === "delete"}
          onClose={() => {
            setActionType();
            setSelectedBook();
          }}
          onDelete={deleteBook}
          title={`Are you sure to delete ${selectedBook?.title} unit?`}
        />
        {Auth.loggedInUser?.id && (
          <Button variant="contained" onClick={() => setActionType("create")}>
            Add new book
          </Button>
        )}
        <Box className="md-homePageBooksSection">
          {isLoadingBooks ? (
            <LinearProgress />
          ) : allBooksStatus === "resolved" && allBooks ? (
            <>
              <Grid
                container
                rowSpacing={2}
                columnSpacing={2}
                className="topSection"
                alignItems="stretch"
              >
                <Grid item xs={12} md={1} className="center-item"></Grid>
                <Grid
                  item
                  xs={12}
                  md={10}
                  sx={{ display: "block", marginTop: 2 }}
                >
                  <Typography variant="h4" sx={{ mt: 4, mb: 2 }} color="primay">
                    List of books
                  </Typography>
                  <Grid
                    className="md-homePageBooksList md-GridListContainer"
                    container
                    item
                    rowSpacing={2}
                    columnSpacing={2}
                    alignItems="stretch"
                    sx={{
                      justifyContent: "left",
                      pl: "0px",
                      pr: 0,
                    }}
                  >
                    {allBooks?.books
                      ?.filter((book) => filterItems(book, searchText))
                      .map((book) => (
                        <Grid
                          item
                          key={book.id}
                          xs={6}
                          md={3}
                          sx={{ width: "100%" }}
                          className="md-gridShowItem"
                        >
                          <ShowItem
                            title={book.title}
                            imageUrl={book.image_url}
                            bgColor={book.bgColor}
                            onClick={() => navigate(`/book/${book.id}`)}
                            onEdit={
                              Auth.loggedInUser?.id
                                ? () => {
                                    setSelectedBook(book);
                                    setActionType("edit");
                                  }
                                : undefined
                            }
                            onDelete={
                              Auth.loggedInUser?.id
                                ? () => {
                                    setSelectedBook(book);
                                    setActionType("delete");
                                  }
                                : undefined
                            }
                          />
                        </Grid>
                      ))}
                  </Grid>
                </Grid>
                <Grid item xs={12} md={1} className="center-item"></Grid>
              </Grid>
            </>
          ) : (
            <Typography variant="h6">Error on fetching data</Typography>
          )}
          {allBooks?.books?.filter((book) =>
            book.title.toLowerCase().includes(searchText.toLowerCase())
          ).length === 0 && (
            <Typography
              className="md-noContent"
              variant="h6"
              textAlign="center"
            >
              No Content Find
            </Typography>
          )}
        </Box>
      </Box>
      <Box
        component="footer"
        sx={{ backgroundColor: (theme) => theme.palette.secondary.main }}
        className="md-footer"
      >
        Copyright © All Rights Reserved By Pirhani Way Academy.
      </Box>
    </Box>
  );
};

export default HomePage;
